import { isNullOrUndefined, isPositiveNumber } from "et3-type-guards";
import { memoize } from "lodash";
import { observable, runInAction } from "mobx";
import { apiCallSetIntervalActive } from "../../../api/core-api/api-call-set-time-interval";

import { TCorePage, TSpentTime } from "../../../Types";

import getCoreConnectionStore from "../core-connection-store";
import getCoreTabStore from "../core-tab-store";
import getCoreHistoryStore from "../core-history-store";
import getRootStore from "../root-store";
import getDashBoardStore from "../../dashboard-store";
import getKhattabatStore from "../../khattabat-stores";
import { getKhattabatResultsStore } from "../../khattabat-stores/results-store";
import {
  KHATTABAT_PAGES_REFRESH_INTERVAL,
  MONITORED_KHATTABAT_PAGES
} from "../../../constants/core-constants";

class CoreTimingStore {
  refreshTimer: ReturnType<typeof setInterval> = null;
  khattabatRefreshTimer: ReturnType<typeof setInterval> = null;
  spentTimeOnPage = observable.box<TSpentTime[]>([]);
  spentTimeOnPageBackUp = observable.box<TSpentTime[]>([]);
  countDownTime = observable.box<number>(600);
  khattabatCountDownTime = observable.box<number>(60);
  currentPageData = observable.map<string, Date>();
  khattabaPageRefreshData = observable.map<TCorePage, number>();

  getPageDates = (oldPageName: TCorePage, pageName: TCorePage) => {
    const startDate = new Date();
    if (oldPageName === "") {
      this.currentPageData.set(pageName, startDate);
      return;
    }
    this.calculateTimeOnPageChange(oldPageName, pageName, false);
  };

  setKhattabatPageData = (pageName: TCorePage, startDate: number) => {
    runInAction(() => {
      this.khattabaPageRefreshData.set(pageName, startDate);
    });
  };
  initializeKhattabatPageMonitoring = (pageName: TCorePage) => {
    const startDate = Date.now();
    getCoreTimingStore().setKhattabatPageData(pageName, startDate);
    getCoreTimingStore().monitorKhattabatPages();
  };
  setKhattabaCountDowntimer = (countDownTime: number) => {
    runInAction(() => {
      this.khattabatCountDownTime.set(countDownTime);
    });
  };

  calculateTimeOnPageChange = (
    oldPageName: TCorePage,
    pageName: TCorePage,
    isFinalPage: boolean
  ) => {
    const startDateForNewOrEndDateForOld = new Date();
    if (!isFinalPage) {
      this.currentPageData.set(pageName, startDateForNewOrEndDateForOld);
    }
    if (!isNullOrUndefined(this.currentPageData.get(oldPageName))) {
      const prevPageStartDate = this.currentPageData.get(oldPageName);
      const activeTimeImMiliseconds = this.caluclateTimeSpentInMilliSeconds(
        prevPageStartDate,
        startDateForNewOrEndDateForOld
      );
      const spentTimeOnPage = this.spentTimeOnPage.get();
      spentTimeOnPage.push({
        pageName: oldPageName,
        startTime: prevPageStartDate,
        endTime: startDateForNewOrEndDateForOld,
        timeSpentInMillisecondsClient: activeTimeImMiliseconds
      });
    }
  };
  sendActiveTimeAndResetData = async () => {
    const pageName = getCoreHistoryStore().pageName.get();
    this.calculateTimeOnPageChange(pageName, pageName, true);
    const timeSpentByEachPage = this.spentTimeOnPage.get();
    // we can clear safely sice saved on back up on function on offline

    this.currentPageData.clear();
    this.spentTimeOnPage.set([]);
    //if not online dont send to server
    if (getCoreConnectionStore().isOnline.get()) {
      if (timeSpentByEachPage.length > 0) {
        await apiCallSetIntervalActive({
          timeSpentOnEachPage: timeSpentByEachPage
        });
      }
    }
  };
  startCalculatingTimeOnFocus = () => {
    if (getCoreConnectionStore().isOnline.get()) {
      const startDate = new Date();
      const pageName = getCoreHistoryStore().pageName.get();
      this.currentPageData.set(pageName, startDate);
    }
  };
  caluclateTimeSpentInMilliSeconds = (firstDate: Date, secondDate: Date) => {
    const startTimeInMS = firstDate.getTime();
    const endTimeInMS = secondDate.getTime();
    const timeSpentInMilliseconds = endTimeInMS - startTimeInMS;
    return timeSpentInMilliseconds;
  };
  monitorKhattabatPages = () => {
    clearInterval(this.khattabatRefreshTimer);
    this.khattabatRefreshTimer = setInterval(async () => {
      const currentDate = Date.now();
      const pageName = getCoreHistoryStore().getPageName();
      if (MONITORED_KHATTABAT_PAGES.includes(pageName)) {
        const lastVisited = this.khattabaPageRefreshData.get(pageName);
        if (
          lastVisited &&
          currentDate - lastVisited >= KHATTABAT_PAGES_REFRESH_INTERVAL
        ) {
          await getDashBoardStore().getKhattabatDashboardData();
          switch (pageName) {
            case "KHATTABAT_M":
            case "KHATTABAT_F":
              const gender = pageName === "KHATTABAT_M" ? "m" : "f";
              await Promise.all([
                getKhattabatStore().getUnreadKhattabaClients({
                  gender: gender
                }),
                getKhattabatStore().updateOpenedClientConversations()
              ]);
              break;
            case "KHATTABAT_MATCHING_RESULTS":
              await Promise.all([
                getKhattabatResultsStore().getUnreadMatchesConversations(),
                getKhattabatResultsStore().updateClientOpenedOneOOneMConversations()
              ]);
              break;
          }
          this.setKhattabatPageData(pageName, currentDate);
        }
      }
    }, 60000);
  };

  onOfflineCalculateDate = () => {
    const pageName = getCoreHistoryStore().pageName.get();
    this.calculateTimeOnPageChange(pageName, pageName, true);
    this.spentTimeOnPageBackUp.set(this.spentTimeOnPage.get());
    this.currentPageData.clear();
    this.spentTimeOnPage.set([]);
  };
  onOnlineSendData = async () => {
    if (this.spentTimeOnPageBackUp.get().length > 0) {
      const timeSpentByEachPage = this.spentTimeOnPageBackUp.get();
      await apiCallSetIntervalActive({
        timeSpentOnEachPage: timeSpentByEachPage
      });

      this.spentTimeOnPageBackUp.set([]);
    }
    if (getCoreTabStore().isFocused.get()) {
      const pageName = getCoreHistoryStore().pageName.get();
      const startDate = new Date();
      this.currentPageData.set(pageName, startDate);
    }
  };
  turnOnRefreshTimer = () => {
    this.refreshTimer = setInterval(async () => {
      const countDownTime = this.countDownTime.get();

      if (isNullOrUndefined(countDownTime)) {
        return;
      }
      if (countDownTime <= 0) {
        this.countDownTime.set(600);
        await getRootStore().refreshData();
      } else {
        runInAction(() => {
          const newCountDownTime = this.countDownTime.get() - 1;
          this.countDownTime.set(newCountDownTime);
        });
      }
    }, 1000);
  };
  turnOnKhattabaRefreshTimer = () => {
    this.refreshTimer = setInterval(async () => {
      const countDownTime = this.khattabatCountDownTime.get();
      if (isNullOrUndefined(countDownTime)) {
        return;
      }
      if (!isPositiveNumber(countDownTime)) {
        this.setKhattabaCountDowntimer(60);
        await getRootStore().refreshData();
      } else {
        runInAction(() => {
          const newCountDownTime = countDownTime - 1;
          this.setKhattabaCountDowntimer(newCountDownTime);
        });
      }
    }, 1000);
  };
  kilTimers = () => {
    if (!isNullOrUndefined(this.refreshTimer)) {
      clearInterval(this.refreshTimer);
      clearInterval(this.khattabatRefreshTimer);
    }
  };
}
const getCoreTimingStore = memoize(
  () => new CoreTimingStore(),
  () => 1
);
export default getCoreTimingStore;

// to Test it
// 1 - Open admin
//   - go to 1 page then go out from admin
//   -  go to infinity pages go out from admin

// 2 - Open admin
//   - go to pages
//   - kill connection while you are on admin
//   - reconnect to connection while u are on admin

// 3 - Open admin
//   - go to pages
//   - kill connection while you are on admin
//   - go out of admin
//   - reconnect to connection while u are outside admin

// 4 - Open admin
//   - go to pages
//   - go out of admin
//   - kill connection while u are outside admin
//   - enter admin
//   - open connection
//   - leave admin

// 5 - Open admin
//   - go to pages
//   - go out of admin
//   - kill connection while u are outside admin
//   - enter admin
//   - leave admin
//   - open connection
